import React, { useState, useMemo } from "react";
import { CuraeCard, HospitalCard } from "@coverme/cards";
import BestFitCard from "../BestFitCard/BestFitCard";
import { getCustomFormattedNameFromObject } from "../../services/stringService";
import "./MarketplaceCards.scss";
import { Link } from "react-router-dom";
import CustomerEnabledFeature from "../CustomerEnabledFeature/CustomerEnabledFeature";
import { LTCCard } from "./LTCCard";
import { formatScore } from "../../services/scoreFormatter";
import "../BestFitCard/BestFitCard.scss";

function getPageTitleAndSubtitle(canUserApproveAnyProgram, patientName, hasLtc) {
    if (hasLtc) {
        return {
            title: "Long Term Care Program Assessment",
            subtitle: "Recommendations are based on the applicant's information and program requirements.",
        };
    } else if (canUserApproveAnyProgram) {
        return {
            title: `Coverage for ${patientName}`,
        };
    } else {
        return {
            title: "Great Job",
            subtitle: (
                <>
                    The patient’s information has been submitted for review. <br />
                    <Link to={"/"}>Click here</Link> to return to the dashboard.
                </>
            ),
        };
    }
}

export const MarketplaceCards = ({
    programs,
    onApproved,
    onDeclined,
    patient,
    canUserApproveAnyProgram,
    customer,
    households,
    notes,
    verifications,
    overallScore,
    fitScore,
    riskScore,
    reliabilityScore,
    caseClues,
    responsibleParty,
}) => {
    const [currentCard, toggleCard] = useState(false);

    const bestFit = programs.filter(program => {
        return program.isBestFit;
    });

    const ltcProgram = programs.filter(program => program.cardTemplate === "LTC")[0];

    const additionalCards = useMemo(() => {
        return programs.filter(program => !program.isBestFit && program !== ltcProgram);
    }, [programs]);

    const onProgramApproved = program => {
        if (onApproved) {
            onApproved(program, program.programDetails.Title === "Credit");
        }
    };

    const onProgramDeclined = program => {
        if (onDeclined) {
            onDeclined(program);
        }
    };

    const renderAdditionalCards = additionalCards.map(program => {
        switch (program.cardTemplate) {
            case "CURAE":
                return (
                    <CuraeCard
                        program={program}
                        detailsEnabled={false}
                        detailsOpen={currentCard === program.name}
                        selectAction={onProgramApproved.bind(this, program)}
                        toggleDetails={() => {
                            if (currentCard === program.name) {
                                toggleCard(false);
                            } else {
                                toggleCard(program.name);
                            }
                        }}
                    />
                );
                break;
            case "HOSPITAL":
                if (program.programDetails.Title === "Credit") {
                    return (
                        <CustomerEnabledFeature requiredFeature="Paya">
                            <HospitalCard
                                program={program}
                                detailsEnabled={false}
                                detailsOpen={currentCard === program.name}
                                onApprove={onProgramApproved.bind(this, program)}
                                onDeny={onProgramDeclined.bind(this, program)}
                                canDeny={program.canCardBeDenied}
                                canApprove={program.requestingUserCanApprove}
                                toggleDetails={() => {
                                    if (currentCard === program.name) {
                                        toggleCard(false);
                                    } else {
                                        toggleCard(program.name);
                                    }
                                }}
                            />
                        </CustomerEnabledFeature>
                    );
                } else {
                    return (
                        <HospitalCard
                            program={program}
                            detailsEnabled={false}
                            detailsOpen={currentCard === program.name}
                            onApprove={onProgramApproved.bind(this, program)}
                            onDeny={onProgramDeclined.bind(this, program)}
                            canDeny={program.canCardBeDenied}
                            canApprove={program.requestingUserCanApprove}
                            approveButtonText={program.requiresPostSelectionApproval ? "Recommend" : "Approve"}
                            toggleDetails={() => {
                                if (currentCard === program.name) {
                                    toggleCard(false);
                                } else {
                                    toggleCard(program.name);
                                }
                            }}
                        />
                    );
                }
            default:
                if (program.programDetails.Title === "Credit") {
                    return (
                        <CustomerEnabledFeature requiredFeature="Paya">
                            <HospitalCard
                                program={program}
                                detailsEnabled={false}
                                detailsOpen={currentCard === program.name}
                                onApprove={onProgramApproved.bind(this, program)}
                                onDeny={onProgramDeclined.bind(this, program)}
                                canDeny={program.canCardBeDenied}
                                canApprove={program.requestingUserCanApprove}
                                toggleDetails={() => {
                                    if (currentCard === program.name) {
                                        toggleCard(false);
                                    } else {
                                        toggleCard(program.name);
                                    }
                                }}
                            />
                        </CustomerEnabledFeature>
                    );
                } else {
                    return (
                        <HospitalCard
                            program={program}
                            detailsEnabled={false}
                            detailsOpen={currentCard === program.name}
                            onApprove={onProgramApproved.bind(this, program)}
                            onDeny={onProgramDeclined.bind(this, program)}
                            canDeny={program.canCardBeDenied}
                            canApprove={program.requestingUserCanApprove}
                            approveButtonText={program.requiresPostSelectionApproval ? "Recommend" : "Approve"}
                            toggleDetails={() => {
                                if (currentCard === program.name) {
                                    toggleCard(false);
                                } else {
                                    toggleCard(program.name);
                                }
                            }}
                        />
                    );
                }
        }
    });

    const fullName = getCustomFormattedNameFromObject(patient, "the patient");
    const { title, subtitle } = useMemo(() => {
        return getPageTitleAndSubtitle(canUserApproveAnyProgram, fullName, ltcProgram != null);
    }, [canUserApproveAnyProgram, fullName, ltcProgram]);

    return (
        <div className="marketplace-cards">
            <div className="marketplace-cards__copy">
                <h2>{title}</h2>
                <p>{subtitle}</p>
            </div>
            <div className="marketplace-cards__long-term-care-container">
                {ltcProgram != null && (
                    <LTCCard
                        households={households}
                        notes={notes}
                        verifications={verifications}
                        patient={patient}
                        program={ltcProgram}
                        onApprove={onProgramApproved.bind(this, ltcProgram)}
                        onDeny={onProgramDeclined.bind(this, ltcProgram)}
                        caseClues={caseClues}
                    />
                )}
            </div>
            {bestFit.length === 1 && (
                <div className="marketplace-cards__best-fit-container">
                    <BestFitCard
                        program={bestFit[0]}
                        onApprove={onProgramApproved.bind(this, bestFit[0])}
                        onDeny={onProgramDeclined.bind(this, bestFit[0])}
                        detailsEnabled={false}
                        detailsOpen={currentCard === bestFit[0]["name"]}
                        canDeny={bestFit[0].canCardBeDenied}
                        canApprove={bestFit[0].requestingUserCanApprove}
                        approveButtonText={bestFit[0].requiresPostSelectionApproval ? "Recommend" : "Approve"}
                        toggleDetails={() => {
                            if (currentCard === bestFit[0]["name"]) {
                                toggleCard(false);
                            } else {
                                toggleCard(bestFit[0]["name"]);
                            }
                        }}
                        overallScore={formatScore(overallScore)}
                        fitScore={formatScore(fitScore)}
                        riskScore={formatScore(riskScore)}
                        reliabilityScore={formatScore(reliabilityScore)}
                        caseClues={caseClues}
                        verifications={verifications}
                        responsibleParty={responsibleParty}
                    />
                </div>
            )}
            {additionalCards.length > 0 && (
                <>
                    <p className="marketplace-cards__label">Additional programs</p>
                    <div className="marketplace-cards__additional-container">{renderAdditionalCards}</div>
                </>
            )}
        </div>
    );
};
